import { useSyncExternalStore } from 'react';

/**
 * O teclado virtual, para quem precisa sair da frente dele.
 *
 * Por que existe, dado que o `index.html` ja pede
 * `interactive-widget=resizes-content`: aquele atributo so e obedecido pelo
 * Chrome do Android. O iOS Safari ignora e mantem o viewport de LAYOUT do
 * tamanho da tela inteira quando o teclado sobe, encolhendo apenas o viewport
 * VISUAL. Ou seja, para o CSS nada mudou, e tudo que esta ancorado embaixo
 * continua desenhado atras do teclado.
 *
 * A unica fonte de verdade nesse caso e `window.visualViewport`.
 *
 * ------------------------------------------------------------------
 * DESENHO: o CSS posiciona, o React so liga e desliga
 * ------------------------------------------------------------------
 * A altura do teclado e publicada numa variavel CSS (`--altura-teclado`) e
 * atualizada a cada quadro da animacao. Quem so precisa SE MOVER usa a
 * variavel e nao re-renderiza nenhuma vez.
 *
 * O React so e avisado quando o teclado ABRE ou FECHA, que e a unica pergunta
 * cuja resposta muda a arvore (esconder a barra de abas, por exemplo). Sem essa
 * separacao seriam ~30 re-renders por abertura de teclado, que e o numero de
 * eventos que o iOS dispara durante a animacao.
 *
 * A subscricao e UNICA para o app inteiro (modulo, nao componente): tres
 * consumidores com tres listeners fariam o mesmo trabalho tres vezes e
 * disputariam a escrita da mesma variavel.
 */

export interface TecladoVirtual {
  /** O teclado esta ocupando a tela? Use para ESCONDER coisa. */
  aberto: boolean;
  /** Altura ocupada, em px. Prefira a variavel CSS para POSICIONAR. */
  altura: number;
}

const VARIAVEL = '--altura-teclado';

/**
 * Abaixo disto nao e teclado.
 *
 * A barra de acessorios do iOS, a barra de endereco do Chrome recolhendo e as
 * transicoes de toolbar do Safari mexem no visualViewport em algumas dezenas de
 * pixels. Tratar isso como "teclado aberto" faria a barra de abas piscar toda
 * vez que a pessoa rola a pagina.
 */
const LIMIAR = 120;

const PARADO: TecladoVirtual = { aberto: false, altura: 0 };

let estado: TecladoVirtual = PARADO;
let inscritos: (() => void)[] = [];
let quadroAgendado: number | null = null;

const publicarVariavel = (altura: number) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(VARIAVEL, `${altura}px`);
};

const medir = () => {
  quadroAgendado = null;
  const vv = window.visualViewport;
  if (!vv) return;

  // `offsetTop` nao e opcional: no iOS, quando a pagina rola por baixo do
  // teclado, o viewport visual se DESLOCA sem redimensionar. Sem este termo a
  // altura oscila e a folha de baixo treme.
  const altura = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));

  // A variavel CSS acompanha cada quadro: e ela que faz o movimento ser suave
  // sem custar render.
  publicarVariavel(altura);

  const aberto = altura > LIMIAR;
  // O React so acorda na virada. Mudanca de altura com o teclado ja aberto nao
  // muda a arvore de ninguem.
  if (aberto !== estado.aberto) {
    estado = { aberto, altura };
    inscritos.forEach((avisar) => avisar());
  } else {
    estado = { ...estado, altura };
  }
};

/**
 * Coalesce a rajada de eventos num unico quadro.
 *
 * `medir` faz uma leitura de layout (`visualViewport.height`) e uma escrita
 * (`setProperty`). Fazer o par a cada evento, ~30 vezes por abertura, e
 * exatamente a receita de layout thrashing.
 */
const aoMudar = () => {
  if (quadroAgendado !== null) return;
  quadroAgendado = requestAnimationFrame(medir);
};

const assinar = (avisar: () => void): (() => void) => {
  inscritos.push(avisar);

  if (inscritos.length === 1 && typeof window !== 'undefined' && window.visualViewport) {
    const vv = window.visualViewport;
    // `resize` sozinho perde as transicoes do iOS que so deslocam; `scroll`
    // sozinho perde o redimensionamento do Android. Precisa dos dois.
    vv.addEventListener('resize', aoMudar);
    vv.addEventListener('scroll', aoMudar);
    // Girar o aparelho deixa o iOS reportando valor velho por um quadro.
    window.addEventListener('orientationchange', aoMudar);
    medir();
  }

  return () => {
    inscritos = inscritos.filter((i) => i !== avisar);
    if (inscritos.length > 0) return;

    if (typeof window !== 'undefined' && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', aoMudar);
      window.visualViewport.removeEventListener('scroll', aoMudar);
      window.removeEventListener('orientationchange', aoMudar);
    }
    if (quadroAgendado !== null) {
      cancelAnimationFrame(quadroAgendado);
      quadroAgendado = null;
    }
    // Zerar na saida NAO e higiene opcional: deixar um valor antigo na variavel
    // manteria toda folha do app flutuando acima de nada.
    estado = PARADO;
    publicarVariavel(0);
  };
};

const ler = (): TecladoVirtual => estado;
const lerNoServidor = (): TecladoVirtual => PARADO;

export function useVirtualKeyboard(): TecladoVirtual {
  return useSyncExternalStore(assinar, ler, lerNoServidor);
}
